import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useInboundUnreadCounts } from '@/hooks/useInboundEmails';
import { contactMessagesService } from '@/services/contactMessagesService';

export const CONTACT_UNREAD_COUNT_KEY = ['contact-unread-count'] as const;

/**
 * Pastille de l'icône Messages : messages Contact non lus + messages ayant une réponse du client non lue (actualisé toutes les 60 s, pas de Realtime).
 * Désactivé sans la permission 'contact_messages' : aucune requête pour les autres utilisateurs.
 * En cas d'erreur : valeur indéfinie (le badge reste masqué), nouvel essai à l'intervalle suivant.
 */
export function useContactUnreadCount() {
  const { hasPermission, isLoading } = usePermissions();
  const allowed = !isLoading && hasPermission('contact_messages');

  const query = useQuery({
    queryKey: CONTACT_UNREAD_COUNT_KEY,
    queryFn: () => contactMessagesService.getUnreadCount(),
    enabled: allowed,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  // Réponses du client non lues : un message compte une fois, même avec plusieurs réponses (la RLS limite aux messages visibles)
  const { counts } = useInboundUnreadCounts();
  const withReply = counts ? Object.keys(counts.contact).length : 0;
  const count =
    allowed && (query.data !== undefined || counts !== undefined)
      ? (query.data ?? 0) + withReply
      : undefined;

  return { allowed, count, isError: query.isError };
}
